import { Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';
import { cn } from '@folio/lib';
import type { DocumentCategorySummaryDto, DocumentFileSummaryDto } from 'korint-sdk';
import { CircleCheckIcon, ClockIcon, TriangleAlertIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { DocumentReviewFilter } from './document-review';

const MAX_LISTED_CATEGORIES = 3;

function SummaryCard({
  icon,
  iconClass,
  count,
  label,
  categories,
  categoryLabel,
  action,
}: {
  icon: ReactNode;
  iconClass: string;
  count: ReactNode;
  label: string;
  categories: DocumentCategorySummaryDto[];
  categoryLabel: (category: DocumentCategorySummaryDto) => string;
  action?: { label: string; onClick: () => void };
}) {
  const { t } = useTranslation();
  const hiddenCount = categories.length - MAX_LISTED_CATEGORIES;

  return (
    <Stack gap={3} className="p-4">
      <Group gap={3}>
        <span
          aria-hidden="true"
          className={cn('flex h-8 w-8 items-center justify-center rounded-md', iconClass)}
        >
          {icon}
        </span>
        <Text inline className="text-lg font-semibold tabular-nums">
          {count}
        </Text>
        <Text inline tone="muted">
          {label}
        </Text>
      </Group>
      <ul className="flex-1 space-y-1 text-sm">
        {categories.length === 0 && (
          <li className="text-muted-foreground">{t('documentReview.fileSummary.none')}</li>
        )}
        {categories.slice(0, MAX_LISTED_CATEGORIES).map(category => (
          <li key={category.category}>{categoryLabel(category)}</li>
        ))}
        {hiddenCount > 0 && (
          <li className="text-muted-foreground">
            {t('documentReview.fileSummary.others', { count: hiddenCount })}
          </li>
        )}
      </ul>
      {action && (
        <button
          type="button"
          className="self-start text-sm font-semibold underline underline-offset-4"
          onClick={action.onClick}
        >
          {action.label}
        </button>
      )}
    </Stack>
  );
}

export function DocumentFileSummary({
  summary,
  categoryLabel,
  onFilterChange,
}: {
  summary: DocumentFileSummaryDto;
  categoryLabel: (category: string) => string;
  onFilterChange: (filter: DocumentReviewFilter) => void;
}) {
  const { t } = useTranslation();
  const required = summary.categories.filter(category => category.required);
  const withStatus = (...statuses: DocumentCategorySummaryDto['status'][]) =>
    required.filter(({ status }) => statuses.includes(status));
  const missing = withStatus('MISSING', 'REJECTED');

  return (
    <section
      aria-label={t('documentReview.fileSummary.title')}
      className="grid rounded-xl border md:grid-cols-3 [&>*+*]:border-t md:[&>*+*]:border-t-0 md:[&>*+*]:border-l"
    >
      <SummaryCard
        icon={<ClockIcon className="h-4 w-4" />}
        iconClass="bg-red-50 text-red-600"
        count={summary.toReview}
        label={t('documentReview.fileSummary.toValidate', { count: summary.toReview })}
        categories={withStatus('TO_REVIEW')}
        categoryLabel={({ category }) => categoryLabel(category)}
        action={{
          label: t('documentReview.fileSummary.validate'),
          onClick: () => onFilterChange('attention'),
        }}
      />
      <SummaryCard
        icon={<TriangleAlertIcon className="h-4 w-4" />}
        iconClass="bg-amber-50 text-amber-700"
        count={missing.length}
        label={t('documentReview.fileSummary.missing', { count: missing.length })}
        categories={missing}
        categoryLabel={({ category, status }) =>
          status === 'REJECTED'
            ? t('documentReview.fileSummary.rejectedCategory', { label: categoryLabel(category) })
            : categoryLabel(category)
        }
        action={{
          label: t('documentReview.fileSummary.showMissing'),
          onClick: () => onFilterChange('missing'),
        }}
      />
      <SummaryCard
        icon={<CircleCheckIcon className="h-4 w-4" />}
        iconClass="bg-emerald-50 text-emerald-700"
        count={
          <>
            {summary.accepted}
            <span className="font-normal text-slate-400"> / {summary.required}</span>
          </>
        }
        label={t('documentReview.fileSummary.validated', { count: summary.accepted })}
        categories={withStatus('ACCEPTED')}
        categoryLabel={({ category }) => categoryLabel(category)}
      />
    </section>
  );
}
